/**
 * Payments — every order this academy has taken, and the way in to one of them.
 *
 * WHY THIS SCREEN EXISTS. Until it did, the only route into the payment-operations app was
 * `GET orders/<order_ref>`: the refund screen had a detail view and no way to arrive at it.
 * An academy admin holding a support email that says "I want a refund" has the learner's name
 * and no order id, and there was no request they could make that turned the first into the
 * second. So this is the entry point to the whole feature, and its job is finding one order.
 *
 * EVERY DECISION THAT MATTERS IS THE SERVER'S, AND THIS SCREEN ONLY DRAWS IT.
 * `admin/api/paymentOps.ts` is the seam and its header lists what it must not re-derive. Two
 * of those bite here specifically:
 *
 *   `writeGate(controls)` decides whether this operator may write anything, and its refusal
 *   sentence is rendered VERBATIM. There are no write controls on this screen, so the banner
 *   is informational — but an operator who is read-only should learn that here rather than
 *   after picking an order, typing a reason and pressing a button that is not there.
 *
 *   `charged_minor` is NULLABLE AND NULL IS NOT ZERO. See `MoneyCell`.
 *
 * THE FILTERS LIVE IN THE URL, deliberately. This is an operations screen and the commonest
 * sentence anybody says about it is "look at this order" — so a filtered list has to be a link
 * a colleague can open, and a reload has to land where the operator was. Same reasoning as the
 * `?path=` and `?course=` query parameters the other admin screens keep.
 *
 * WHAT IS NOT REACHABLE FROM HERE, said plainly rather than left to be discovered: the tabs
 * cover five of the twelve payment states. `pending`, `expired` and `cancelled` — orders where
 * no money ever moved — have no cut of their own, so they appear only under "All". They are
 * not folded into "Failed" because a checkout somebody abandoned is not a failure anybody
 * needs to act on, and mixing them in would bury the refunds that are genuinely stuck.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  EmptyState,
  Icon,
  SearchField,
  SelectField,
  Spinner,
  Tabs,
  Toolbar,
  type Column,
} from '../../shared/ui';
import { formatMoney } from '../../shared/lib/pricing';
import {
  explainOpsFailure,
  loadOrders,
  orderListKey,
  writeGate,
  type OrderListFilters,
  type WireOrderSummary,
} from '../api/paymentOps';
import { PageHeader } from '../AdminShell';
import '../payments/payments.css';

/** Where one order is worked on. Exported so nothing else has to re-spell the encoding. */
export const orderHref = (orderRef: string) =>
  `/admin/payments/${encodeURIComponent(orderRef)}`;

/**
 * Long enough that typing a learner's name is one request rather than nine, short enough that
 * it still feels like the list is following the box.
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The page size we ASK for. What gets applied is `limit` in the response, and that is the
 * number the pager arithmetic uses — the server clamps to 1..100 and echoes what it sliced
 * with, so a client that paged on its own request would skip rows whenever the two differed.
 */
const REQUESTED_LIMIT = 25;

interface StateTab {
  id: string;
  label: string;
  /** Exact payment states, sent as repeated `payment_state` parameters. Empty means no filter. */
  states: readonly string[];
}

const ALL_TAB: StateTab = { id: 'all', label: 'All', states: [] };

const PAYMENT_TABS: readonly StateTab[] = [
  ALL_TAB,
  { id: 'paid', label: 'Paid', states: ['paid'] },
  // Its own tab because an FPX refund can sit here for weeks, and this is the list an operator
  // opens to answer "what have we asked Stripe for that has not landed?".
  { id: 'refund_pending', label: 'Refund pending', states: ['refund_pending'] },
  // Both, because a partial refund is a refunded order to everyone except an accountant, and
  // an operator looking for "did we refund this?" would otherwise miss half the answers.
  { id: 'refunded', label: 'Refunded', states: ['refunded', 'partially_refunded'] },
  // `refund_failed` ONLY. See the file header: the never-paid states are not failures.
  { id: 'failed', label: 'Failed', states: ['refund_failed'] },
];

const tabFor = (id: string): StateTab => PAYMENT_TABS.find((tab) => tab.id === id) ?? ALL_TAB;

/** `AccessState` in `openedx_thinlms_payment_ops.models`, in the order that model declares. */
const ACCESS_OPTIONS: [string, string][] = [
  ['', 'Any access state'],
  ['none', 'Nothing granted yet'],
  ['granting', 'Being granted'],
  ['granted', 'Granted'],
  ['partially_granted', 'Partly granted'],
  ['blocked_upstream', 'Blocked upstream'],
  ['revoke_pending', 'Being revoked'],
  ['revoked', 'Revoked'],
  ['revoke_failed', 'Revoke failed'],
  ['regranted', 'Restored'],
];

const PAYMENT_LABELS: Record<string, string> = {
  pending: 'Awaiting payment',
  paid: 'Paid',
  refund_pending: 'Refund requested',
  partially_refunded: 'Partly refunded',
  refunded: 'Refunded',
  refund_failed: 'Refund failed',
  disputed: 'Disputed',
  dispute_won: 'Dispute won',
  dispute_lost: 'Dispute lost',
  expired: 'Checkout expired',
  cancelled: 'Abandoned',
  no_charge: 'No charge',
};

/**
 * `refund_pending` IS NOT A SUCCESS TONE, and that is the whole reason this map is explicit.
 *
 * "A Stripe refund exists and is not yet terminal" — the money may never leave. Painting it
 * green next to `refunded` would tell an operator the learner has been paid back on the
 * evidence that we asked. Same rule as `OPS_COPY.requested`.
 */
const PAYMENT_TONES: Record<string, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  pending: 'neutral',
  paid: 'success',
  refund_pending: 'info',
  partially_refunded: 'warning',
  refunded: 'neutral',
  refund_failed: 'danger',
  disputed: 'danger',
  dispute_won: 'success',
  dispute_lost: 'danger',
  expired: 'neutral',
  cancelled: 'neutral',
  no_charge: 'neutral',
};

const ACCESS_LABELS: Record<string, string> = {
  none: 'No access',
  granting: 'Being granted',
  granted: 'Access granted',
  partially_granted: 'Partly granted',
  blocked_upstream: 'Blocked upstream',
  revoke_pending: 'Being revoked',
  revoked: 'Access revoked',
  revoke_failed: 'Revoke failed',
  regranted: 'Access restored',
};

const ACCESS_TONES: Record<string, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  none: 'neutral',
  granting: 'info',
  granted: 'success',
  // "Fulfilment is incomplete -- never a settled state", per the model's own comment.
  partially_granted: 'warning',
  blocked_upstream: 'danger',
  revoke_pending: 'info',
  revoked: 'neutral',
  revoke_failed: 'danger',
  regranted: 'success',
};

/** A state string we have no label for is shown as it arrived, never as "Unknown". */
const humanize = (value: string) =>
  value ? value.replaceAll('_', ' ').replace(/^./, (first) => first.toUpperCase()) : '—';

/**
 * An amount, or the honest absence of one.
 *
 * `charged_minor` IS NULL WHEN NOTHING EVER SETTLED, and rendering that as `RM 0.00` is the
 * "404 resolved to free" bug in a new costume: a zero in a money column reads as a free order
 * or a completed refund, and both are statements about money that nobody made. An em dash in a
 * muted span says the same thing the payload says — there is no charge here — and the note
 * under the table spells it out once for the whole screen.
 *
 * `refundable_minor` is 0-COERCED ON THE SERVER (`list_views._row` passes
 * `charged_minor or 0`), so on a never-settled order it arrives as a real zero that means
 * "inapplicable" rather than "nothing left". That is why the caller gates this cell on
 * `charged_minor` as well: RM 0.00 in the refundable column of an unpaid order reads as
 * "already fully refunded".
 */
function MoneyCell({ minor, currency }: { minor: number | null; currency: string }) {
  if (minor === null) return <span className="admin-muted">—</span>;
  if (!currency) {
    // A number with no currency is not a price. Shown, because the amount is real and an
    // operator can still compare it — but never dressed in a symbol we were not sent.
    return (
      <span>
        {formatMoney(minor, currency)}
        <small className="admin-sub">currency not recorded</small>
      </span>
    );
  }
  return <>{formatMoney(minor, currency)}</>;
}

/** A date, in the operator's own locale. `null` is a fact, not a blank. */
function PaidOn({ value }: { value: string | null }) {
  if (!value) return <span className="admin-muted">Not paid</span>;
  const date = new Date(value);
  return <>{Number.isNaN(date.getTime()) ? value : date.toLocaleDateString()}</>;
}

export default function AdminPayments() {
  const [params, setParams] = useSearchParams();

  const q = params.get('q') ?? '';
  const tab = tabFor(params.get('payment') ?? 'all');
  const access = params.get('access') ?? '';
  const offset = Number.parseInt(params.get('offset') ?? '', 10);
  const requestedOffset = Number.isFinite(offset) && offset > 0 ? offset : 0;

  /**
   * One writer for the whole query string, so no caller can set a filter and forget the page.
   *
   * ANY FILTER CHANGE RESETS `offset`. Without that, narrowing a 200-order list while on page
   * four asks the server for rows 75-99 of a result set with eleven rows in it and gets an
   * empty table — which reads as "this learner bought nothing" rather than "you are past the
   * end". That is the same class of mistake as an empty `payment_state` meaning "no filter".
   */
  const update = (patch: Record<string, string>, keepPage = false) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(patch)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        if (!keepPage) next.delete('offset');
        return next;
      },
      // Replaced rather than pushed: a filter is a correction to where you are, and Back
      // should leave the screen rather than walk backwards through nine keystrokes.
      { replace: true },
    );
  };

  // The box is local and the URL is debounced behind it, so typing stays instant while the
  // shareable state settles once. Seeded from the URL, so an opened link fills the box.
  const [typed, setTyped] = useState(q);

  useEffect(() => {
    if (typed.trim() === q) return;
    const timer = setTimeout(() => update({ q: typed.trim() }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // `update` is deliberately not a dependency: it is recreated on every render and closes
    // over nothing but `setParams`, so listing it would restart the timer each render and the
    // debounce would never fire.
  }, [typed, q]);

  // Kept out of the render body so the query key is stable across renders that change
  // nothing about the request.
  const filters: OrderListFilters = useMemo(
    () => ({
      q,
      paymentStates: tab.states,
      accessStates: access ? [access] : [],
      limit: REQUESTED_LIMIT,
      offset: requestedOffset,
    }),
    [q, tab.states, access, requestedOffset],
  );

  const orders = useQuery({
    queryKey: orderListKey(filters),
    queryFn: () => loadOrders(filters),
    retry: false,
  });

  const data = orders.data;
  const rows = data?.orders ?? [];

  /**
   * The APPLIED page, not the requested one.
   *
   * `page_bounds` clamps `limit` to 1..100 and `offset` to >= 0 and the response echoes what
   * it sliced with, precisely so a client can page correctly after asking for something silly.
   * Paging on `REQUESTED_LIMIT` instead would step by 25 through pages of 100 and show the
   * same rows four times.
   */
  const appliedLimit = data?.limit ?? REQUESTED_LIMIT;
  const appliedOffset = data?.offset ?? requestedOffset;
  const total = data?.total ?? 0;
  const firstShown = rows.length === 0 ? 0 : appliedOffset + 1;
  const lastShown = appliedOffset + rows.length;

  const filtered = q !== '' || tab.states.length > 0 || access !== '';

  /**
   * Whether this operator may write anywhere in payment operations.
   *
   * Read only once the response is in hand: `writeGate(undefined)` is `unknown` by design —
   * "absent payload is not permission" — and rendering its fallback sentence while the first
   * request is still in flight would announce a refusal that may not exist.
   */
  const gate = data ? writeGate(data.controls) : null;

  const failure = orders.isError ? explainOpsFailure(orders.error) : null;

  const columns: Column<WireOrderSummary>[] = [
    {
      key: 'learner',
      header: 'Learner',
      render: (order) => (
        <>
          <Link to={orderHref(order.order_ref)}>{order.learner.username || order.order_ref}</Link>
          <div className="admin__sub">{order.learner.email}</div>
        </>
      ),
    },
    {
      key: 'item',
      header: 'Bought',
      render: (order) => (
        <>
          <Badge tone="neutral">{order.item.kind || 'unknown'}</Badge>
          <div className="admin__sub">{order.item.key}</div>
        </>
      ),
    },
    {
      key: 'charged',
      header: 'Charged',
      numeric: true,
      render: (order) => (
        <MoneyCell minor={order.charged_minor} currency={order.charge_currency} />
      ),
    },
    {
      key: 'refundable',
      header: 'Refundable',
      numeric: true,
      render: (order) => (
        <MoneyCell
          minor={order.charged_minor === null ? null : order.refundable_minor}
          currency={order.charge_currency}
        />
      ),
    },
    {
      key: 'payment',
      header: 'Payment',
      render: (order) => (
        <Badge tone={PAYMENT_TONES[order.payment_state] ?? 'neutral'}>
          {PAYMENT_LABELS[order.payment_state] ?? humanize(order.payment_state)}
        </Badge>
      ),
    },
    {
      key: 'access',
      header: 'Access',
      render: (order) => (
        <Badge tone={ACCESS_TONES[order.access_state] ?? 'neutral'}>
          {ACCESS_LABELS[order.access_state] ?? humanize(order.access_state)}
        </Badge>
      ),
    },
    { key: 'paid', header: 'Paid', render: (order) => <PaidOn value={order.paid_at} /> },
    {
      key: 'open',
      header: 'Actions',
      render: (order) => (
        <div className="admin__row-actions">
          <Link className="ui-btn" data-variant="ghost" to={orderHref(order.order_ref)}>
            Open
          </Link>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Payments"
        description="Every order this academy has taken. Find one to refund it, remove access, or read what has already been done to it."
      />

      {/* The server's own sentence, unedited. The three refusals have three different next
          actions — the academy is not enabled, a mandate is missing, you hold no authority —
          and a paraphrase would send somebody to the wrong person. */}
      {gate && gate.kind !== 'writable' && (
        <Banner tone="info">
          <strong>{gate.reason}</strong> Nothing on these screens will offer to move money or
          change access while that is the case.
        </Banner>
      )}

      <div className="ops-list">
        <Toolbar>
          <Tabs
            label="Which orders to show"
            active={tab.id}
            /* NO `count` PROP. The component's own note says a count of 0 and "not loaded
               yet" differ, and the only count this screen ever knows is `total` for the
               filter currently applied — putting that number on one tab would label the
               other four with a figure that is not theirs. */
            tabs={PAYMENT_TABS.map((entry) => ({ id: entry.id, label: entry.label }))}
            onChange={(id) => update({ payment: id === 'all' ? '' : id })}
          />
          <div className="toolbar__end">
            <SearchField
              value={typed}
              onChange={setTyped}
              placeholder="Search by learner, email or order reference…"
            />
          </div>
        </Toolbar>

        <div className="ops-list__filters">
          <SelectField
            label="Access state"
            name="payments-access-state"
            value={access}
            options={ACCESS_OPTIONS}
            onChange={(event) => update({ access: event.target.value })}
          />
        </div>

        {orders.isPending ? (
          <Spinner label="Loading this academy's orders…" />
        ) : failure ? (
          /* Rendered here rather than in place of the whole screen, so a filter that caused
             the refusal is still on screen and still changeable. */
          <Banner tone="danger">
            <strong>{failure.message}</strong>
            {failure.nextStep && <p className="admin-note">{failure.nextStep}</p>}
          </Banner>
        ) : rows.length === 0 ? (
          filtered ? (
            /* Two sentences, because they are two different facts. With a filter applied we
               do not know whether this academy has any orders — only that none of them match
               — so this one must not claim the academy has none. */
            <EmptyState
              title="No orders match this filter"
              body="Nothing in this academy matches what you have asked for. Clearing the filter shows every order it has taken."
              action={
                <Button
                  variant="secondary"
                  onClick={() => {
                    setTyped('');
                    update({ q: '', payment: '', access: '' });
                  }}
                >
                  <Icon name="filter_alt_off" />
                  Clear the filter
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="This academy has no orders yet"
              body="An order appears here the moment a learner starts a paid checkout, whether or not the payment goes through."
            />
          )
        ) : (
          <Card>
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(order) => order.order_ref}
              empty={null}
            />

            {/* Said once for the whole table rather than as a tooltip on every cell. An em
                dash is a load-bearing value here and an operator has to be able to read it. */}
            <p className="admin-note">
              An em dash in Charged or Refundable means no payment ever settled on that order.
              It is not the same as RM 0.00, which would mean money changed hands and none of it
              is left to refund.
            </p>

            <div className="admin__pager">
              <Button
                variant="ghost"
                disabled={appliedOffset <= 0}
                onClick={() =>
                  update(
                    { offset: String(Math.max(0, appliedOffset - appliedLimit)) },
                    true,
                  )
                }
              >
                <Icon name="chevron_left" />
                Previous
              </Button>
              <span className="ops-list__count">
                Showing {firstShown}–{lastShown} of {total}
              </span>
              <Button
                variant="ghost"
                disabled={lastShown >= total}
                onClick={() => update({ offset: String(appliedOffset + appliedLimit) }, true)}
              >
                Next
                <Icon name="chevron_right" />
              </Button>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
